import type { IImage } from 'src/types/image';

import { defaultImage } from 'src/types/constants';

const bucketURL = import.meta.env.VITE_BUCKET_URL;

export const getImageUrl = (image: IImage | undefined) => {
  if (!image) return defaultImage;
  return `${bucketURL}/images/${image?.s3Guid}/original.webp`;
};

export const getImageUrlByS3Guid = (s3Guid: string | undefined) => {
  if (!s3Guid) return defaultImage;
  return `${bucketURL}/images/${s3Guid}/original.webp`;
};

export const getImageThumbnail = (imageS3Guid: string | undefined) => {
  if (!imageS3Guid) return defaultImage;
  return `${bucketURL}/images/${imageS3Guid}/thumbnail.webp`;
};

export const getImageVerticalPreview = (imageS3Guid: string | undefined) => {
  if (!imageS3Guid) return defaultImage;
  return `${bucketURL}/images/${imageS3Guid}/vertical.webp`;

};

export const getVideoUrl = (video: { url: string; s3Guid: string } | undefined) =>
  `${bucketURL}/video/${video?.s3Guid}/${video?.s3Guid}.mp4`;

export const getAudioUrl = (audioS3Guid: string | undefined) =>
  `${bucketURL}/audio/${audioS3Guid}.mp3`;

export const AMUSE_MAX_VIDEO_SIZE = 100 * 1024 * 1024; // 100MB
export const AMUSE_SUPPORTED_VIDEO_FORMATS = {
  'video/mp4': ['.mp4'],
  'video/x-msvideo': ['.avi'],
  'video/x-matroska': ['.mkv'],
  'video/quicktime': ['.mov'],
  'video/x-flv': ['.flv'],
  'video/x-ms-wmv': ['.wmv'],
  'video/webm': ['.webm'],
  'video/hevc': ['.hevc', '.h265'],
  'video/h264': ['.h264'],
};

export const AMUSE_MAX_AUDIO_SIZE = 10 * 1024 * 1024; // 10MB
export const AMUSE_SUPPORTED_AUDIO_FORMATS = {
  'audio/mpeg': ['.mp3'],
  'audio/aac': ['.aac'],
  'audio/wav': ['.wav'],
  'audio/flac': ['.flac'],
  'audio/ogg': ['.ogg'],
  'audio/x-ms-wma': ['.wma'],
};

export const AMUSE_MAX_IMAGE_SIZE = 15 * 1024 * 1024; // 15MB
export const AMUSE_SUPPORTED_IMAGE_FORMATS = {
  'image/png': ['.png'],
  'image/jpeg': ['.jpeg'],
  'image/jpg': ['.jpg'],
  'image/webp': ['.webp'],
  'image/svg+xml': ['.svg'],
};
export const AMUSE_SUPPORTED_BATCH_UPLOAD_FORMATS = {
  'text/csv': ['.csv'],
  'text/geojson': ['.geojson'],
};

/**
 * Get file extension from Content-Type header or MIME type
 * @param contentType - The Content-Type header value
 * @param fallbackUrl - Optional URL to extract extension from as fallback
 * @returns The file extension (without dot)
 */
export const getFileExtensionFromContentType = (
  contentType: string | null,
  fallbackUrl?: string
): string => {
  if (contentType) {
    const mimeType = contentType.toLowerCase().split(';')[0]; // Remove charset if present

    switch (mimeType) {
      case 'image/jpeg':
      case 'image/jpg':
        return 'jpg';
      case 'image/png':
        return 'png';
      case 'image/webp':
        return 'webp';
      case 'image/gif':
        return 'gif';
      case 'image/bmp':
        return 'bmp';
      case 'image/tiff':
        return 'tiff';
      case 'image/svg+xml':
        return 'svg';
      default:
        break;
    }
  }

  // Fallback: try to extract from URL if Content-Type is not helpful
  if (fallbackUrl) {
    const urlExtension = fallbackUrl.split('.').pop()?.split('?')[0]?.split('#')[0];
    if (
      urlExtension &&
      ['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp', 'tiff', 'svg'].includes(
        urlExtension.toLowerCase()
      )
    ) {
      return urlExtension.toLowerCase();
    }
  }

  return 'image'; // Default fallback
};
